import React from 'react';
import {
  Observer,
  observer,
  useLocalObservable,
} from 'mobx-react-lite';

import { Separator } from '../common/Separator';
import { IListViewProps, ListView } from '../views/ListView';
import {
  Comment,
  CommentRefModelType,
} from '@models/Comments/CommentModel';
import { CommentItem, ICommentItemProps } from './CommentItem';
import { CommentInputModeEnum } from './CommentInput';
import { User } from '@models/Viewer/UserModel';

export interface ICommentListStore {
  id: number | null;
  mode: CommentInputModeEnum;
  setId(id: number, mode: CommentInputModeEnum): void;
  deleteId(): void;
  isInUpdateMode(id: number): boolean;
  isInCreateMode(id: number): boolean;
}

interface ICommentListProps
  extends Omit<IListViewProps<CommentRefModelType>, 'renderItem'> {
  onPressReply?(item: Comment): void;
  onPressEdit?(item: Comment): void;
  commentInputSize?: ICommentItemProps['commentInputSize'];
  onPressUser?(user: User): void;
}

export const CommentList = observer<ICommentListProps>(
  ({
    data,
    onPressReply,
    onPressEdit,
    commentInputSize,
    onPressUser,
    ...props
  }) => {
    const commentListStore = useLocalObservable<ICommentListStore>(
      () => ({
        id: null,
        mode: CommentInputModeEnum.Create,

        setId(id: number, mode: CommentInputModeEnum) {
          this.id = id;
          this.mode = mode;
        },

        deleteId() {
          this.id = null;
          this.mode = CommentInputModeEnum.Create;
        },

        isInUpdateMode(id: number) {
          return (
            this.id === id &&
            this.mode === CommentInputModeEnum.Update
          );
        },

        isInCreateMode(id: number) {
          return (
            this.id === id &&
            this.mode === CommentInputModeEnum.Create
          );
        },
      }),
    );

    return (
      <ListView<CommentRefModelType>
        keyExtractor={(i) => i.id.toString()}
        data={data}
        renderItem={({ item }) => (
          <Observer>
            {() => (
              <CommentItem
                onPressUser={onPressUser}
                item={item.current}
                onPressReply={onPressReply}
                onPressEdit={onPressEdit}
                commentListStore={commentListStore}
                commentInputSize={commentInputSize}
              />
            )}
          </Observer>
        )}
        ListHeaderComponent={Separator}
        ItemSeparatorComponent={() => (
          <Separator size={['$5', '$6']} />
        )}
        {...props}
      />
    );
  },
);
